import React, { forwardRef } from 'react'
import clsx from 'clsx'

import './SectionTitle.scss'

type headingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

interface SectionTitleProps {
  align?: 'start' | 'center' | 'end'
  caption?: string
  children?: React.ReactNode
  className?: string
  dataTestId?: string
  description?: string
  fullWidth?: boolean
  inversed?: boolean
  title: string
  /** When set, overrides the default `title-1` / `title-2` / … class derived from `titleTag` (e.g. `h1` with `title-2`). */
  titleTypeClass?: 'title-1' | 'title-2' | 'title-3' | 'title-4'
  titleTag?: headingTag
  type?: 'primary' | 'secondary'
}

const SectionTitle = forwardRef<HTMLElement, SectionTitleProps>(
  (
    { align = 'center', caption, children, className, dataTestId, description, fullWidth, inversed = false, title, titleTypeClass, titleTag = 'h2', type = 'primary' },
    ref,
  ) => {
    const TitleTag = titleTag
    const defaultTitleTypeClass = (() => {
      switch (TitleTag) {
        case 'h1':
          return 'title-1'
        case 'h2':
          return 'title-2'
        case 'h4':
          return 'title-4'
        default:
          return 'title-3'
      }
    })()
    const titleClass = titleTypeClass ?? defaultTitleTypeClass
    const alignClass = `section-title--${align}`
    return (
      <hgroup
        className={clsx(
          `section-title ${alignClass}`,
          {
            'section-title--full-width': fullWidth,
            'section-title--inversed': inversed,
            'section-title--secondary': type === 'secondary',
          },
          className,
        )}>
        {type === 'secondary' ? (
          <>
            <TitleTag className={clsx('section-title__title', titleClass)} data-testid={dataTestId}>{title}</TitleTag>
            {caption && <p className="section-title__caption caption-1">{caption}</p>}
            {description && <p className="section-title__description body-2">{description}</p>}
          </>
        ) : (
          <>
            {caption && <p className="section-title__caption caption-1">{caption}</p>}
            <TitleTag className={clsx('section-title__title', titleClass)} data-testid={dataTestId}>{title}</TitleTag>
            {description && <p className="section-title__description body-2">{description}</p>}
          </>
        )}
      </hgroup>
    )
  },
)

export default SectionTitle
